// apps/web/src/components/astral/AstralMiniPreview.tsx
import { useMemo } from "react";
import type { AstralMapData, AstralNode } from "../../types/astral";

interface AstralMiniPreviewProps {
  data?: AstralMapData | any;
  layout?: string;
  theme?: string;
  title?: string;
}

interface VisualChild {
  text: string;
  grandchildren: string[];
}

interface VisualStructure {
  rootText: string;
  children: VisualChild[];
}

const DEFAULT_THEME_ACCENT = {
  bg: "#0f0505",
  nodeBg: "#24100f",
  rootBg: "#c2a06b",
  line: "rgba(194, 160, 107, 0.4)",
  text: "#f8fafc",
};

const THEME_ACCENTS: Record<string, { bg: string; nodeBg: string; rootBg: string; line: string; text: string }> = {
  sellflux: DEFAULT_THEME_ACCENT,
  dark: {
    bg: "#080c14",
    nodeBg: "#172033",
    rootBg: "#3b82f6",
    line: "rgba(59, 130, 246, 0.4)",
    text: "#e2e8f0",
  },
  meister: {
    bg: "#0a1120",
    nodeBg: "#1e293b",
    rootBg: "#10b981",
    line: "rgba(16, 185, 129, 0.4)",
    text: "#f1f5f9",
  },
  ocean: {
    bg: "#041426",
    nodeBg: "#0d2b45",
    rootBg: "#0ea5e9",
    line: "rgba(14, 165, 233, 0.4)",
    text: "#e0f2fe",
  },
  sunset: {
    bg: "#160b1e",
    nodeBg: "#2d123d",
    rootBg: "#ec4899",
    line: "rgba(236, 72, 153, 0.4)",
    text: "#fce7f3",
  },
  midnight: {
    bg: "#020408",
    nodeBg: "#0f172a",
    rootBg: "#8b5cf6",
    line: "rgba(139, 92, 246, 0.4)",
    text: "#f8fafc",
  },
  vintage: {
    bg: "#120e0a",
    nodeBg: "#291f19",
    rootBg: "#f59e0b",
    line: "rgba(245, 158, 11, 0.4)",
    text: "#fef3c7",
  },
  bubbles: {
    bg: "#081414",
    nodeBg: "#112e2e",
    rootBg: "#14b8a6",
    line: "rgba(20, 184, 166, 0.4)",
    text: "#ccfbf1",
  },
  blackboard: {
    bg: "#09090b",
    nodeBg: "#18181b",
    rootBg: "#22c55e",
    line: "rgba(34, 197, 94, 0.4)",
    text: "#f4f4f5",
  },
};

export function AstralMiniPreview({ data, layout = "map", theme = "sellflux", title }: AstralMiniPreviewProps) {
  const themeColors = (theme && THEME_ACCENTS[theme]) ? THEME_ACCENTS[theme]! : DEFAULT_THEME_ACCENT;

  const parsedData = useMemo<AstralMapData | null>(() => {
    if (!data) return null;
    if (typeof data === "object" && data.nodes && data.rootId) return data as AstralMapData;
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        if (parsed && parsed.nodes && parsed.rootId) return parsed as AstralMapData;
      } catch {
        return null;
      }
    }
    return null;
  }, [data]);

  const structure = useMemo<VisualStructure>(() => {
    if (!parsedData) {
      return {
        rootText: title || "Mapa Mental",
        children: [
          { text: "Estratégia", grandchildren: ["Etapa 1", "Etapa 2"] },
          { text: "Operação", grandchildren: ["Execução"] },
          { text: "Resultados", grandchildren: ["Métricas"] },
        ],
      };
    }

    const rootNode = parsedData.nodes[parsedData.rootId];
    const rootText = rootNode?.text || title || "Mapa Mental";

    const allNodes = Object.values(parsedData.nodes);
    const directChildren = allNodes
      .filter((n: AstralNode) => n.parentId === parsedData.rootId)
      .slice(0, 4);

    const children: VisualChild[] = directChildren.map((child: AstralNode) => {
      const grandchildren = allNodes
        .filter((n: AstralNode) => n.parentId === child.id)
        .slice(0, 2)
        .map((g: AstralNode) => g.text || "Item");
      return {
        text: child.text || "Tópico",
        grandchildren,
      };
    });

    return { rootText, children };
  }, [parsedData, title]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: themeColors.bg,
        borderRadius: "8px",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg
        viewBox="0 0 360 160"
        style={{
          width: "100%",
          height: "100%",
          display: "block",
        }}
      >
        <defs>
          <linearGradient id={`grad-root-${theme}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={themeColors.rootBg} stopOpacity="0.95" />
            <stop offset="100%" stopColor={themeColors.rootBg} stopOpacity="0.75" />
          </linearGradient>
        </defs>

        {layout === "org" ? (
          // VISUALIZAÇÃO HIERÁRQUICA ORGANOGRAMA
          <g>
            {/* Raiz no Topo */}
            <rect
              x="130"
              y="18"
              width="100"
              height="28"
              rx="6"
              fill={`url(#grad-root-${theme})`}
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="1"
            />
            <text
              x="180"
              y="36"
              textAnchor="middle"
              fill="#ffffff"
              fontSize="10"
              fontWeight="bold"
            >
              {structure.rootText.slice(0, 14)}
            </text>

            {/* Linha Vertical Raiz */}
            <path
              d="M 180 46 L 180 62"
              fill="none"
              stroke={themeColors.line}
              strokeWidth="1.5"
            />

            {/* Filhos Abaixo */}
            {structure.children.slice(0, 3).map((c, i) => {
              const xPos = 40 + i * 115;
              return (
                <g key={i}>
                  <path
                    d={`M 180 62 L ${xPos + 40} 62 L ${xPos + 40} 78`}
                    fill="none"
                    stroke={themeColors.line}
                    strokeWidth="1.2"
                  />
                  <rect
                    x={xPos}
                    y="78"
                    width="80"
                    height="24"
                    rx="5"
                    fill={themeColors.nodeBg}
                    stroke="rgba(255,255,255,0.12)"
                    strokeWidth="1"
                  />
                  <text
                    x={xPos + 40}
                    y="94"
                    textAnchor="middle"
                    fill={themeColors.text}
                    fontSize="8.5"
                  >
                    {c.text.slice(0, 11)}
                  </text>

                  {/* Netos */}
                  {c.grandchildren.slice(0, 1).map((gText, gi) => (
                    <g key={gi}>
                      <path
                        d={`M ${xPos + 40} 102 L ${xPos + 40} 118`}
                        fill="none"
                        stroke={themeColors.line}
                        strokeWidth="1"
                      />
                      <rect
                        x={xPos + 10}
                        y="118"
                        width="60"
                        height="18"
                        rx="4"
                        fill="rgba(255,255,255,0.04)"
                        stroke="rgba(255,255,255,0.06)"
                        strokeWidth="1"
                      />
                      <text
                        x={xPos + 40}
                        y="130"
                        textAnchor="middle"
                        fill="rgba(255,255,255,0.4)"
                        fontSize="7"
                      >
                        {gText.slice(0, 9)}
                      </text>
                    </g>
                  ))}
                </g>
              );
            })}
          </g>
        ) : (
          // VISUALIZAÇÃO MAPA RADIAL
          <g>
            {/* Raiz Central */}
            <rect
              x="130"
              y="66"
              width="100"
              height="28"
              rx="6"
              fill={`url(#grad-root-${theme})`}
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="1"
            />
            <text
              x="180"
              y="84"
              textAnchor="middle"
              fill="#ffffff"
              fontSize="10"
              fontWeight="bold"
            >
              {structure.rootText.slice(0, 14)}
            </text>

            {/* Ramos Laterais */}
            {structure.children.map((c, i) => {
              const isRight = i % 2 === 0;
              const yOffset = i < 2 ? 38 : 122;
              const targetX = isRight ? 260 : 25;
              const nodeW = 75;

              const startX = isRight ? 230 : 130;
              const startY = 80;
              const endX = isRight ? targetX : targetX + nodeW;
              const endY = yOffset + 12;

              const cpx1 = startX + (isRight ? 25 : -25);
              const cpx2 = endX + (isRight ? -25 : 25);

              return (
                <g key={i}>
                  <path
                    d={`M ${startX} ${startY} C ${cpx1} ${startY}, ${cpx2} ${endY}, ${endX} ${endY}`}
                    fill="none"
                    stroke={themeColors.line}
                    strokeWidth="1.3"
                  />
                  <rect
                    x={targetX}
                    y={yOffset}
                    width={nodeW}
                    height="24"
                    rx="5"
                    fill={themeColors.nodeBg}
                    stroke="rgba(255,255,255,0.12)"
                    strokeWidth="1"
                  />
                  <text
                    x={targetX + nodeW / 2}
                    y={yOffset + 16}
                    textAnchor="middle"
                    fill={themeColors.text}
                    fontSize="8.5"
                  >
                    {c.text.slice(0, 10)}
                  </text>
                </g>
              );
            })}
          </g>
        )}
      </svg>
    </div>
  );
}
